import { type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@ttab/elephant-ui'
import { XIcon } from '@ttab/elephant-ui/icons'
import type { Block } from '@ttab/elephant-api/newsdoc'
import type { MemoLine } from '@/shared/templates/memos/lib/renderMemo'
import { PreviewLine } from './PreviewLine'

/**
 * An already written PM folded into this memo, as the preview shows it.
 *
 * It is a run of lines the editor did not write here and cannot rearrange
 * here, so it carries the two controls that do apply to it: taking the whole
 * PM back out, and dropping any single line of it - four of the night's five
 * without retyping any of them.
 */
export const PreviewDocument = ({ title, lines, onRemove, onRemoveLine }: {
  title: string | undefined
  lines: MemoLine[]
  onRemove: () => void
  onRemoveLine: (block: Block | undefined) => void
}): JSX.Element => {
  const { t } = useTranslation()

  return (
    <div className='pt-1'>
      <div className='flex items-center justify-between gap-2'>
        <span className='min-w-0 truncate text-xs font-medium text-muted-foreground'>
          {title}
        </span>
        <Button
          variant='ghost'
          size='sm'
          className='size-6 shrink-0 p-0 text-muted-foreground'
          aria-label={t('memo:actions.removeInsertedDocument', { title: title ?? '' })}
          onClick={onRemove}
        >
          <XIcon size={14} strokeWidth={1.75} />
        </Button>
      </div>

      {lines.map((line, index) => (
        <div key={index} className='flex items-start gap-2'>
          <Button
            variant='ghost'
            size='sm'
            className='size-6 shrink-0 p-0 text-muted-foreground'
            aria-label={t('memo:actions.removeInsertedLine', { text: line.text })}
            onClick={() => onRemoveLine(line.block)}
          >
            <XIcon size={14} strokeWidth={1.75} />
          </Button>

          <div className='min-w-0 grow'>
            <PreviewLine line={line} />
          </div>
        </div>
      ))}
    </div>
  )
}
